import { useMutation } from '@tanstack/react-query';

import { type Invitation } from '@sine/sine-core-api-types';
import { useNotification } from '@sine/sine-ui-components';

import parseServerError from '../../../helpers/parseServerError';
import { MutationKeys } from '../../../query.client';
import invitationsService from '../../../services/invitation/invitations.service';
import type { InitialInvitationValues, InviteesRef } from '../Invitations.types';
import { composeInvitations } from '../helpers/composeInvitations';

interface UseUpdateInvitationProps {
  onClose: VoidFunction;
}

export function useUpdateInvitation({ onClose }: UseUpdateInvitationProps) {
  const { addNotification } = useNotification();

  const { mutateAsync: updateInvitation, isLoading: isLoadingUpdate } = useMutation({
    mutationKey: [MutationKeys.UPDATE_INVITATION],
    mutationFn: async ({
      existingInvitation,
      inviteesFormRef,
      invitationDetails,
      canSendEmail,
    }: {
      existingInvitation: Invitation;
      inviteesFormRef: InviteesRef;
      invitationDetails: InitialInvitationValues['invitationDetails'];
      canSendEmail: boolean;
    }) => {
      const composedInvitation = (await composeInvitations(inviteesFormRef, invitationDetails))[0];

      const mergedInvitation: Invitation = { ...existingInvitation, ...composedInvitation };

      await invitationsService.updateInvitation(mergedInvitation, canSendEmail);
    },
    onSuccess: () => {
      addNotification({
        heading: 'Invitation edited',
        variant: 'success',
      });
      onClose();
    },
    onError: async (error: any) => {
      let heading;
      let shouldCloseModal = true;

      switch (error.data.code) {
        case 'INVALID_INVITATION':
          error.customMsg = `Your invitation start and end date exceeds the 5 year limit. Please adjust the invitation to fit within this limit and try again.`;
          shouldCloseModal = false;
          break;

        case 'INVITATION_NOT_PERMITTED':
          heading = 'Unable to edit the invitation';
          error.customMsg = 'Your changes to the invitation were blocked. Check your email for details.';
          shouldCloseModal = true;
          break;

        default:
          heading = 'Error updating invitation';
          shouldCloseModal = false;
          break;
      }

      addNotification({
        heading: heading,
        message: parseServerError(error),
        variant: 'danger',
      });

      shouldCloseModal && onClose();
    },
  });

  return { updateInvitation, isLoadingUpdate };
}
